import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  RefreshCw, Unlink, ExternalLink, AlertCircle, CheckCircle2, X,
  ChevronRight, ChevronLeft, Server, Cloud, Eye, EyeOff, HelpCircle,
  Zap, ArrowRight, User, AlertTriangle, PauseCircle, FileClock,
} from 'lucide-react';
import {
  listIntegrations,
  getIntegrationConnectUrl,
  disconnectIntegration,
  syncIntegration,
  cancelSync,
  testJiraDCConnection,
  connectJiraDC,
  updateIntegrationSchedule,
  listPersonalIntegrations,
  getPersonalGoogleConnectUrl,
  disconnectPersonalGoogle,
  getIntegrationHealth,
  setPersonalGoogleVisibility,
  getPersonalGoogleIngestedContent,
  type IntegrationHealthStatus,
  type GoogleVisibilityLevel,
  type IngestedContentItem,
} from '../../lib/api';
import { queryKeys } from '../../lib/queryKeys';
import { ConfirmModal } from '../ConfirmModal';
import { ProviderMark } from '../ProviderMark';
import type { Integration, PersonalIntegration } from '../../types';

// Brand marks render through ProviderMark — the app's single decision
// point for light/dark logo variants. Do not add logo paths here.
const PROVIDER_INFO: Record<
  Integration['provider'],
  { name: string; description: string; stub?: boolean }
> = {
  slack: {
    name: 'Slack',
    description: 'Sync conversations and signal alerts to channels',
  },
  jira: {
    name: 'Jira',
    description: 'Pull tickets, sprints, and blockers automatically',
  },
  google: {
    name: 'Google Workspace',
    description: 'Connect Docs, Drive, and Calendar data',
    // Same as Slack above — _sync_google is an unimplemented stub.
    stub: true,
  },
  linear: {
    name: 'Linear',
    description: 'Sync Linear issues and project cycles',
  },
};

// A single derived status per card, computed once from every underlying
// signal (connected flag, health doc, in-flight sync, overdue detection),
// with an explicit precedence. Every badge/pill/banner/button on the card
// reads from this ONE value — never from the raw signals independently —
// so the card can't render contradictory claims about itself (e.g. a green
// "Connected" badge next to an amber "Reconnect required" pill after a
// sync that just succeeded, which is what prompted this).
export type ProviderCardStatus =
  | 'disconnected'
  | 'syncing'
  | 'needs-reconnect'
  | 'paused'
  | 'degraded'
  | 'connected';

function deriveCardStatus(
  integration: Pick<Integration, 'isConnected' | 'syncStatus' | 'syncOverdue'>,
  healthStatus: IntegrationHealthStatus | undefined,
): ProviderCardStatus {
  if (!integration.isConnected) return 'disconnected';
  if (integration.syncStatus === 'syncing') return 'syncing';
  // healthStatus 'stale' means the backend proved the credential is broken
  // — either a dedicated auth probe (workers/token_health.py) or a sync
  // request that was actually rejected with 401/403 (workers/sync.py). It
  // takes precedence over everything except an in-flight sync, because a
  // proven auth failure is the one thing here that always needs action.
  if (healthStatus === 'stale') return 'needs-reconnect';
  if (healthStatus === 'paused') return 'paused';
  // Not a proven auth failure, but something short of a clean connected
  // state: a generic sync error, or a sync that's overdue by well beyond
  // its configured interval (computed fresh on every read — see
  // syncOverdue on the Integration type).
  if (integration.syncOverdue || integration.syncStatus === 'error') return 'degraded';
  return 'connected';
}

// ---------------------------------------------------------------------------
// Shared modals
// ---------------------------------------------------------------------------

function SyncPromptModal({ providerName, onSync, onSkip, loading }: {
  providerName: string; onSync: () => void; onSkip: () => void; loading?: boolean;
}) {
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onSkip} />
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.15 }}
        className="relative bg-white dark:bg-gray-900 border border-gray-200 dark:border-white/10 rounded-2xl shadow-xl p-6 w-full max-w-sm z-10"
      >
        <div className="flex justify-center mb-4">
          <div className="h-12 w-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
            <CheckCircle2 className="h-6 w-6 text-green-600 dark:text-green-400" />
          </div>
        </div>
        <h3 className="text-title text-gray-900 dark:text-white text-center mb-2">{providerName} connected!</h3>
        <p className="text-body text-gray-500 dark:text-gray-400 text-center mb-5">
          Would you like to run an initial sync now to pull in your data?
        </p>
        <div className="flex gap-2">
          <button onClick={onSkip} className="flex-1 px-4 py-2 text-meta rounded-lg bg-gray-100 dark:bg-white/10 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-white/15 transition-colors">
            Not now
          </button>
          <button
            onClick={onSync}
            disabled={loading}
            className="flex-1 px-4 py-2 text-meta rounded-lg font-medium bg-primary text-white hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
          >
            {loading ? <><RefreshCw className="h-4 w-4 animate-spin" />Syncing…</> : 'Sync Now'}
          </button>
        </div>
      </motion.div>
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Jira Data Center wizard
// ---------------------------------------------------------------------------

type DCWizardStep = 'choose' | 'quick' | 'instance' | 'credentials' | 'success';

interface DCWizardProps {
  onConnected: () => void;
  onCloudInstead: () => void;
  onClose: () => void;
}

function JiraDCWizard({ onConnected, onCloudInstead, onClose }: DCWizardProps) {
  const qc = useQueryClient();
  const [step, setStep] = useState<DCWizardStep>('choose');
  const [instanceUrl, setInstanceUrl] = useState('');
  const [email, setEmail] = useState('');
  const [apiToken, setApiToken] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [testStatus, setTestStatus] = useState<'idle' | 'testing' | 'ok' | 'error'>('idle');
  const [testError, setTestError] = useState<string | null>(null);
  const [testUser, setTestUser] = useState<string | null>(null);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [instanceName, setInstanceName] = useState('');

  const testMutation = useMutation({
    mutationFn: () => testJiraDCConnection(instanceUrl.trim(), email.trim(), apiToken.trim()),
    onMutate: () => { setTestStatus('testing'); setTestError(null); },
    onSuccess: (data) => {
      setTestStatus('ok');
      setTestUser(data.displayName ?? data.emailAddress ?? email);
    },
    onError: (err: Error) => {
      setTestStatus('error');
      setTestError(err.message || 'Connection test failed.');
    },
  });

  const connectMutation = useMutation({
    mutationFn: () => connectJiraDC(instanceUrl.trim(), email.trim(), apiToken.trim()),
    onSuccess: (data) => {
      setInstanceName(data.instanceName);
      setStep('success');
      void qc.invalidateQueries({ queryKey: queryKeys.integrations });
    },
    onError: (err: Error) => {
      setConnectError(err.message || 'Connection failed.');
    },
  });

  const slideVariants = {
    enter: (dir: number) => ({ x: dir * 40, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir: number) => ({ x: dir * -40, opacity: 0 }),
  };
  const [slideDir, setSlideDir] = useState(1);

  const goTo = (next: DCWizardStep, dir = 1) => {
    setSlideDir(dir);
    setStep(next);
  };

  const stepIndex: Record<DCWizardStep, number> = { choose: 0, quick: 0, instance: 1, credentials: 2, success: 3 };
  const totalSteps = 3; // choose/quick don't count as numbered steps

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={step === 'success' ? undefined : onClose}
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 16 }}
        transition={{ duration: 0.2 }}
        className="relative bg-white dark:bg-bg-surface border border-gray-200 dark:border-white/10 rounded-2xl shadow-2xl w-full max-w-md z-10 overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-100 dark:border-white/[0.06]">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-lg bg-[#0052CC]/10 flex items-center justify-center">
              <ProviderMark provider="jira" className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-title text-gray-900 dark:text-white">Connect Jira</h3>
              {step !== 'choose' && step !== 'quick' && step !== 'success' && (
                <p className="text-micro text-gray-400">
                  Step {stepIndex[step]} of {totalSteps}
                </p>
              )}
              {step === 'quick' && (
                <p className="text-micro text-gray-400">Quick setup</p>
              )}
            </div>
          </div>
          {step !== 'success' && (
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-white transition-colors">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Step progress bar (not shown on choose/quick/success) */}
        {step !== 'choose' && step !== 'quick' && step !== 'success' && (
          <div className="h-1 bg-gray-100 dark:bg-white/5">
            <motion.div
              className="h-full bg-[#0052CC]"
              initial={false}
              animate={{ width: `${(stepIndex[step] / totalSteps) * 100}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        )}

        {/* Step content */}
        <div className="overflow-hidden">
          <AnimatePresence mode="wait" custom={slideDir}>
            {step === 'choose' && (
              <motion.div
                key="choose"
                custom={slideDir}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.2 }}
                className="px-6 py-5 space-y-4"
              >
                <p className="text-body text-gray-600 dark:text-gray-400">
                  How is your Jira hosted?
                </p>

                <button
                  onClick={onCloudInstead}
                  className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-gray-200 dark:border-white/10 hover:border-primary/50 hover:bg-primary/[0.02] transition-all text-left group"
                >
                  <div className="h-10 w-10 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
                    <Cloud className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="flex-1">
                    <p className="text-body font-semibold text-gray-900 dark:text-white">Jira Cloud</p>
                    <p className="text-meta text-gray-500 dark:text-gray-400 mt-0.5">
                      Hosted by Atlassian — your URL ends in .atlassian.net
                    </p>
                  </div>
                  <ArrowRight className="h-4 w-4 text-gray-300 dark:text-gray-600 group-hover:text-primary transition-colors" />
                </button>

                <button
                  onClick={() => goTo('instance')}
                  className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-gray-200 dark:border-white/10 hover:border-primary/50 hover:bg-primary/[0.02] transition-all text-left group"
                >
                  <div className="h-10 w-10 rounded-xl bg-gray-100 dark:bg-white/5 flex items-center justify-center flex-shrink-0">
                    <Server className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                  </div>
                  <div className="flex-1">
                    <p className="text-body font-semibold text-gray-900 dark:text-white">Jira Data Center</p>
                    <p className="text-meta text-gray-500 dark:text-gray-400 mt-0.5">
                      Self-hosted on your own infrastructure
                    </p>
                  </div>
                  <ArrowRight className="h-4 w-4 text-gray-300 dark:text-gray-600 group-hover:text-primary transition-colors" />
                </button>

                <div className="flex items-center justify-between pt-1">
                  <p className="text-micro text-gray-400 dark:text-gray-500">
                    Not sure? Check if your URL contains .atlassian.net
                  </p>
                  <button
                    onClick={() => goTo('quick')}
                    className="flex items-center gap-1 text-meta text-primary hover:text-primary/80 transition-colors whitespace-nowrap"
                  >
                    <Zap className="h-4 w-4" />
                    Quick setup
                  </button>
                </div>
              </motion.div>
            )}

            {step === 'quick' && (
              <motion.div
                key="quick"
                custom={slideDir}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.2 }}
                className="px-6 py-5 space-y-4"
              >
                <p className="text-meta text-gray-500 dark:text-gray-400">
                  Enter your Data Center credentials — all in one step.
                </p>

                <div>
                  <label className="block text-meta font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                    Instance URL
                  </label>
                  <input
                    type="url"
                    value={instanceUrl}
                    onChange={(e) => { setInstanceUrl(e.target.value); setTestStatus('idle'); }}
                    placeholder="https://jira.yourcompany.com"
                    autoFocus
                    className="w-full px-3 py-2.5 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white text-body placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30"
                  />
                </div>

                <div>
                  <label className="block text-meta font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                    Email address
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setTestStatus('idle'); }}
                    placeholder="you@company.com"
                    className="w-full px-3 py-2.5 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white text-body placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-meta font-medium text-gray-600 dark:text-gray-400">
                      API token
                    </label>
                    {instanceUrl.trim().startsWith('http') && (
                      <a
                        href={`${instanceUrl.trim()}/secure/ViewProfile.jspa`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-meta text-primary hover:text-primary/80 transition-colors"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <HelpCircle className="h-4 w-4" />
                        Get token
                      </a>
                    )}
                  </div>
                  <div className="relative">
                    <input
                      type={showToken ? 'text' : 'password'}
                      value={apiToken}
                      onChange={(e) => { setApiToken(e.target.value); setTestStatus('idle'); }}
                      placeholder="Personal access token"
                      className="w-full px-3 py-2.5 pr-10 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white text-body placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30 font-mono"
                    />
                    <button
                      type="button"
                      onClick={() => setShowToken((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    >
                      {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {/* Test / connect feedback */}
                <AnimatePresence>
                  {testStatus === 'ok' && testUser && (
                    <motion.div
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-900/30 text-meta text-green-700 dark:text-green-400"
                    >
                      <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
                      Connected as <span className="font-medium">{testUser}</span>
                    </motion.div>
                  )}
                  {testStatus === 'error' && testError && (
                    <motion.div
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      className="flex items-start gap-2 px-3 py-2.5 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/30 text-meta text-red-600 dark:text-red-400"
                    >
                      <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                      {testError}
                    </motion.div>
                  )}
                  {connectError && (
                    <motion.div
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      className="flex items-start gap-2 px-3 py-2.5 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/30 text-meta text-red-600 dark:text-red-400"
                    >
                      <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                      {connectError}
                    </motion.div>
                  )}
                </AnimatePresence>

                <div className="flex gap-2 pt-1">
                  <button
                    onClick={() => goTo('choose', -1)}
                    className="flex items-center gap-1 px-4 py-2.5 text-meta rounded-xl border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Back
                  </button>

                  {testStatus !== 'ok' ? (
                    <button
                      onClick={() => testMutation.mutate()}
                      disabled={
                        !instanceUrl.trim().startsWith('http') ||
                        !email.trim() ||
                        !apiToken.trim() ||
                        testStatus === 'testing'
                      }
                      className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 text-meta rounded-xl border border-primary/40 text-primary hover:bg-primary/5 transition-colors disabled:opacity-40 font-medium"
                    >
                      {testStatus === 'testing' ? (
                        <><RefreshCw className="h-4 w-4 animate-spin" />Testing…</>
                      ) : (
                        <><Zap className="h-4 w-4" />Test Connection</>
                      )}
                    </button>
                  ) : (
                    <button
                      onClick={() => connectMutation.mutate()}
                      disabled={connectMutation.isPending}
                      className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 text-meta rounded-xl bg-primary text-white hover:bg-primary/90 transition-colors disabled:opacity-40 font-medium"
                    >
                      {connectMutation.isPending ? (
                        <><RefreshCw className="h-4 w-4 animate-spin" />Connecting…</>
                      ) : (
                        <>Connect<ChevronRight className="h-4 w-4" /></>
                      )}
                    </button>
                  )}
                </div>
              </motion.div>
            )}

            {step === 'instance' && (
              <motion.div
                key="instance"
                custom={slideDir}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.2 }}
                className="px-6 py-5 space-y-4"
              >
                <div>
                  <p className="text-body font-medium text-gray-900 dark:text-white mb-0.5">Instance URL</p>
                  <p className="text-meta text-gray-500 dark:text-gray-400 mb-3">
                    Enter the base URL of your Jira Data Center instance.
                  </p>
                  <input
                    type="url"
                    value={instanceUrl}
                    onChange={(e) => setInstanceUrl(e.target.value)}
                    placeholder="https://jira.yourcompany.com"
                    autoFocus
                    className="w-full px-3 py-2.5 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white text-body placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30"
                  />
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/20 rounded-xl p-5 text-meta text-blue-700 dark:text-blue-400 space-y-1">
                  <p className="font-medium">Requirements</p>
                  <ul className="space-y-1 ml-2">
                    <li>• Must use HTTPS</li>
                    <li>• Must be reachable from the internet (or allow OctoPM's IPs)</li>
                    <li>• Jira Data Center 8.x or newer</li>
                  </ul>
                </div>

                <div className="flex gap-2 pt-1">
                  <button
                    onClick={() => goTo('choose', -1)}
                    className="flex items-center gap-1 px-4 py-2.5 text-meta rounded-xl border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Back
                  </button>
                  <button
                    onClick={() => goTo('credentials')}
                    disabled={!instanceUrl.trim().startsWith('http')}
                    className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 text-meta rounded-xl bg-primary text-white hover:bg-primary/90 transition-colors disabled:opacity-40 font-medium"
                  >
                    Continue
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </motion.div>
            )}

            {step === 'credentials' && (
              <motion.div
                key="credentials"
                custom={slideDir}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.2 }}
                className="px-6 py-5 space-y-4"
              >
                <div>
                  <p className="text-body font-medium text-gray-900 dark:text-white mb-0.5">Your credentials</p>
                  <p className="text-meta text-gray-500 dark:text-gray-400 mb-3">
                    OctoPM needs read access to pull issues and project data.
                  </p>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-meta font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                      Email address
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); setTestStatus('idle'); }}
                      placeholder="you@company.com"
                      autoFocus
                      className="w-full px-3 py-2.5 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white text-body placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30"
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-meta font-medium text-gray-600 dark:text-gray-400">
                        API token
                      </label>
                      <a
                        href={`${instanceUrl.trim()}/secure/ViewProfile.jspa`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-meta text-primary hover:text-primary/80 transition-colors"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <HelpCircle className="h-4 w-4" />
                        How to get a token
                      </a>
                    </div>
                    <div className="relative">
                      <input
                        type={showToken ? 'text' : 'password'}
                        value={apiToken}
                        onChange={(e) => { setApiToken(e.target.value); setTestStatus('idle'); }}
                        placeholder="Your personal access token"
                        className="w-full px-3 py-2.5 pr-10 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white text-body placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30 font-mono"
                      />
                      <button
                        type="button"
                        onClick={() => setShowToken((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                      >
                        {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    <p className="text-micro text-gray-400 dark:text-gray-500 mt-1.5">
                      Profile → Personal Access Tokens → Create token
                    </p>
                  </div>
                </div>

                {/* Test result */}
                <AnimatePresence>
                  {testStatus === 'ok' && testUser && (
                    <motion.div
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-900/30 text-meta text-green-700 dark:text-green-400"
                    >
                      <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
                      Connected as <span className="font-medium">{testUser}</span>
                    </motion.div>
                  )}
                  {testStatus === 'error' && testError && (
                    <motion.div
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      className="flex items-start gap-2 px-3 py-2.5 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/30 text-meta text-red-600 dark:text-red-400"
                    >
                      <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                      {testError}
                    </motion.div>
                  )}
                  {connectError && (
                    <motion.div
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      className="flex items-start gap-2 px-3 py-2.5 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/30 text-meta text-red-600 dark:text-red-400"
                    >
                      <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                      {connectError}
                    </motion.div>
                  )}
                </AnimatePresence>

                <div className="flex gap-2 pt-1">
                  <button
                    onClick={() => goTo('instance', -1)}
                    className="flex items-center gap-1 px-4 py-2.5 text-meta rounded-xl border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Back
                  </button>

                  {testStatus !== 'ok' ? (
                    <button
                      onClick={() => testMutation.mutate()}
                      disabled={!email.trim() || !apiToken.trim() || testStatus === 'testing'}
                      className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 text-meta rounded-xl border border-primary/40 text-primary hover:bg-primary/5 transition-colors disabled:opacity-40 font-medium"
                    >
                      {testStatus === 'testing' ? (
                        <><RefreshCw className="h-4 w-4 animate-spin" />Testing…</>
                      ) : (
                        <><Zap className="h-4 w-4" />Test Connection</>
                      )}
                    </button>
                  ) : (
                    <button
                      onClick={() => connectMutation.mutate()}
                      disabled={connectMutation.isPending}
                      className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 text-meta rounded-xl bg-primary text-white hover:bg-primary/90 transition-colors disabled:opacity-40 font-medium"
                    >
                      {connectMutation.isPending ? (
                        <><RefreshCw className="h-4 w-4 animate-spin" />Connecting…</>
                      ) : (
                        <>Connect<ChevronRight className="h-4 w-4" /></>
                      )}
                    </button>
                  )}
                </div>
              </motion.div>
            )}

            {step === 'success' && (
              <motion.div
                key="success"
                custom={slideDir}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.2 }}
                className="px-6 py-8 text-center space-y-4"
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 20, delay: 0.1 }}
                  className="mx-auto h-16 w-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center"
                >
                  <CheckCircle2 className="h-8 w-8 text-green-600 dark:text-green-400" />
                </motion.div>
                <div>
                  <h3 className="text-title text-gray-900 dark:text-white">
                    Jira Data Center connected
                  </h3>
                  <p className="text-body text-gray-500 dark:text-gray-400 mt-1">
                    {instanceName || instanceUrl} is ready to sync.
                  </p>
                </div>
                <button
                  onClick={onConnected}
                  className="w-full py-2.5 px-4 text-meta rounded-xl bg-primary text-white hover:bg-primary/90 transition-colors font-medium"
                >
                  Done
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Provider card
// ---------------------------------------------------------------------------

interface ProviderCardProps {
  integration: Integration;
  isNewlyConnected: boolean;
  verifyFailed: boolean;
  onSyncPromptDismissed: () => void;
  healthStatus?: IntegrationHealthStatus;
}

function ProviderCard({ integration, isNewlyConnected, verifyFailed, onSyncPromptDismissed, healthStatus }: ProviderCardProps) {
  const qc = useQueryClient();
  const info = PROVIDER_INFO[integration.provider];
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [showCancelSyncConfirm, setShowCancelSyncConfirm] = useState(false);
  const [showDCWizard, setShowDCWizard] = useState(false);
  const [disconnectError, setDisconnectError] = useState<string | null>(null);
  const [localSchedule, setLocalSchedule] = useState<string>(integration.syncSchedule ?? '1h');

  const isJira = integration.provider === 'jira';
  const isDC = isJira && integration.jiraType === 'datacenter';
  const isStubProvider = !!info.stub;

  const connectMutation = useMutation({
    mutationFn: () => getIntegrationConnectUrl(integration.provider),
    onSuccess: ({ url }) => { window.location.href = url; },
  });

  const disconnectMutation = useMutation({
    mutationFn: () => disconnectIntegration(integration.provider),
    onSuccess: () => {
      setShowDisconnectConfirm(false);
      void qc.invalidateQueries({ queryKey: queryKeys.integrations });
    },
    onError: (err: Error) => { setDisconnectError(err.message || 'Disconnect failed.'); },
  });

  const syncMutation = useMutation({
    mutationFn: () => syncIntegration(integration.provider),
    onSuccess: () => {
      onSyncPromptDismissed();
      void qc.invalidateQueries({ queryKey: queryKeys.integrations });
    },
  });

  const cancelSyncMutation = useMutation({
    mutationFn: () => cancelSync(integration.provider),
    onSettled: () => {
      setShowCancelSyncConfirm(false);
      void qc.invalidateQueries({ queryKey: queryKeys.integrations });
    },
  });

  const scheduleMutation = useMutation({
    mutationFn: (syncSchedule: string) =>
      updateIntegrationSchedule(integration.provider, syncSchedule),
    onError: () => {
      // Revert local state on failure
      setLocalSchedule(integration.syncSchedule ?? '1h');
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.integrations }),
  });

  const formatSyncTime = (ts: string | null) => ts ? new Date(ts).toLocaleString() : 'Never';
  const cardStatus = deriveCardStatus(integration, healthStatus);
  const isSyncing = cardStatus === 'syncing';
  const isStale = cardStatus === 'needs-reconnect';
  const isDegraded = cardStatus === 'degraded';

  const handleConnect = () => {
    if (isJira) {
      setShowDCWizard(true);
    } else {
      connectMutation.mutate();
    }
  };

  const handleReconnect = () => {
    // Re-initiates OAuth without disconnecting first; the successful callback
    // overwrites the old token in Secret Manager.
    if (isJira) {
      setShowDCWizard(true);
    } else {
      connectMutation.mutate();
    }
  };

  const handleDCWizardConnected = () => {
    setShowDCWizard(false);
    void qc.invalidateQueries({ queryKey: queryKeys.integrations });
  };

  const handleCloudInstead = () => {
    setShowDCWizard(false);
    connectMutation.mutate();
  };

  // Metadata subtitle for connected state
  const connectedSubtitle = (() => {
    if (!integration.isConnected) return null;
    if (isDC) {
      const name = integration.metadata.instanceName || integration.metadata.instanceUrl || '';
      const ver = integration.metadata.dcVersion ? ` v${integration.metadata.dcVersion}` : '';
      return name ? `${name}${ver}` : null;
    }
    return integration.metadata.workspaceName || integration.metadata.siteName || null;
  })();

  return (
    <>
      <div className="glass-card rounded-xl p-5">
        <div className="flex items-start gap-4">
          <div className="h-10 w-10 rounded-xl bg-white dark:bg-white/10 flex items-center justify-center flex-shrink-0 overflow-hidden border border-gray-100 dark:border-transparent">
            <ProviderMark provider={integration.provider} alt={info.name} className="h-6 w-6" />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="text-title text-gray-900 dark:text-white">{info.name}</h4>
                  {isDC && (
                    <span className="flex items-center gap-1 text-meta px-1.5 py-0.5 rounded-md bg-gray-100 dark:bg-white/10 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-white/10 font-medium">
                      <Server className="h-2.5 w-2.5" />
                      Data Center
                    </span>
                  )}
                  {isJira && !isDC && integration.isConnected && (
                    <span className="flex items-center gap-1 text-meta px-1.5 py-0.5 rounded-md bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border border-blue-100 dark:border-blue-900/30 font-medium">
                      <Cloud className="h-2.5 w-2.5" />
                      Cloud
                    </span>
                  )}
                  {isStale && (
                    <span
                      className="flex items-center gap-1 text-meta px-1.5 py-0.5 rounded-md bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400 border border-yellow-200 dark:border-yellow-900/30 font-medium"
                      title="Authorization expired — please reconnect"
                    >
                      <AlertTriangle className="h-2.5 w-2.5" />
                      Reconnect required
                    </span>
                  )}
                  {isStubProvider && (
                    <span
                      className="flex items-center gap-1 text-meta px-1.5 py-0.5 rounded-md bg-gray-100 dark:bg-white/10 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-white/10 font-medium"
                      title="Sync for this provider is not implemented yet — connecting will not pull in any data"
                    >
                      Preview — sync not yet available
                    </span>
                  )}
                </div>
                <p className="text-meta text-gray-500 dark:text-gray-400 mt-0.5">{info.description}</p>
              </div>

              <span className={`text-meta px-2 py-0.5 rounded-full border flex-shrink-0 flex items-center gap-1 ${
                cardStatus === 'disconnected'
                  ? 'bg-gray-100 dark:bg-gray-800 text-gray-500 border-gray-200 dark:border-gray-700'
                  : cardStatus === 'paused'
                    ? 'bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-white/10'
                    : cardStatus === 'needs-reconnect' || cardStatus === 'degraded'
                      ? 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400 border-yellow-200 dark:border-yellow-900/30'
                      : 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 border-green-200 dark:border-green-900/30'
              }`}>
                {cardStatus === 'disconnected' ? (
                  'Not Connected'
                ) : cardStatus === 'paused' ? (
                  <><PauseCircle className="h-4 w-4" />Connected · Paused</>
                ) : cardStatus === 'needs-reconnect' ? (
                  <><AlertTriangle className="h-4 w-4" />Reconnect required</>
                ) : cardStatus === 'degraded' ? (
                  <><AlertTriangle className="h-4 w-4" />Needs attention</>
                ) : cardStatus === 'syncing' ? (
                  <><RefreshCw className="h-4 w-4 animate-spin" />Syncing</>
                ) : (
                  <><CheckCircle2 className="h-4 w-4" />Connected</>
                )}
              </span>
            </div>

            {integration.isConnected && (
              <div className="mt-2 text-meta text-gray-500 dark:text-gray-400 space-y-0.5">
                {connectedSubtitle && <p>{connectedSubtitle}</p>}
                {isDC && integration.metadata.email && (
                  <p>Account: {integration.metadata.email}</p>
                )}
                {/* Linear connects as the OAuth app (actor: "app" — see
                    _build_oauth_url in backend/app/routers/integrations.py),
                    not as a specific human, so its "accountEmail" is always
                    a synthetic identifier like
                    <uuid>@oauthapp.linear.app — a raw machine identifier,
                    not something a human recognises. Never render it; the
                    workspace name above (connectedSubtitle) is the
                    meaningful identity here. */}
                {!isDC && integration.provider !== 'linear' && integration.metadata.accountEmail && (
                  <p>Account: {integration.metadata.accountEmail}</p>
                )}
                <p>Last synced: {formatSyncTime(integration.lastSyncedAt)}</p>
              </div>
            )}

            {integration.isConnected && (
              <div className="mt-3 flex items-center gap-2">
                <label className="text-meta text-gray-500 dark:text-gray-400 flex-shrink-0">
                  Sync every
                </label>
                <select
                  value={localSchedule}
                  onChange={(e) => {
                    setLocalSchedule(e.target.value);
                    scheduleMutation.mutate(e.target.value);
                  }}
                  disabled={scheduleMutation.isPending}
                  className="text-meta px-2 py-1 rounded-lg bg-gray-100 dark:bg-white/10 border border-gray-200 dark:border-white/10 text-gray-700 dark:text-gray-300 focus:outline-none focus:border-primary/50 disabled:opacity-50"
                >
                  <option value="30m">30 minutes</option>
                  <option value="1h">1 hour</option>
                  <option value="2h">2 hours</option>
                  <option value="4h">4 hours</option>
                  <option value="24h">24 hours</option>
                  <option value="paused">Paused</option>
                </select>
              </div>
            )}

            {integration.syncStatus === 'error' && integration.lastSyncError && (
              <div className="mt-2 flex items-start gap-1.5 text-meta text-red-400 bg-red-900/20 border border-red-900/30 rounded-lg p-2">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                {integration.lastSyncError}
              </div>
            )}

            {disconnectError && (
              <div className="mt-2 flex items-start gap-1.5 text-meta text-red-400 bg-red-900/20 border border-red-900/30 rounded-lg p-2">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                {disconnectError}
              </div>
            )}

            {isNewlyConnected && verifyFailed ? (
              <div className="mt-2 flex items-start gap-1.5 text-meta text-yellow-700 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-900/30 rounded-lg p-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                The reconnect completed, but we could not verify the new credential works.
                {info.name} may have skipped issuing a fresh token. Try reconnecting again — if
                this keeps happening, disconnect and reconnect from scratch.
              </div>
            ) : isStale ? (
              <div className="mt-2 flex items-start gap-1.5 text-meta text-yellow-700 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-900/30 rounded-lg p-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                This integration returned an authorization error. Reconnect to resume syncing.
              </div>
            ) : (
              isDegraded && (
                <div className="mt-2 flex items-start gap-1.5 text-meta text-yellow-700 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-900/30 rounded-lg p-2">
                  <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  {integration.syncOverdue
                    ? "This integration hasn't synced in longer than expected. The credential may still be fine — try Sync Now, or Reconnect if it keeps failing."
                    : 'The last sync hit an error unrelated to authorization. It will retry on its normal schedule, or you can Sync Now.'}
                </div>
              )
            )}

            {integration.isConnected && isStubProvider && (
              <div className="mt-2 flex items-start gap-1.5 text-meta text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-white/10 border border-gray-200 dark:border-white/10 rounded-lg p-2">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                {info.name} sync isn't implemented yet. The connection is saved, but no data is
                being pulled in — "Connected" here doesn't mean it's syncing.
              </div>
            )}

            <div className="flex items-center gap-2 mt-3 flex-wrap">
              {integration.isConnected && isStale && (
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={handleReconnect}
                  disabled={connectMutation.isPending}
                  className="flex items-center gap-1.5 text-meta px-3 py-2 rounded-lg bg-yellow-100 dark:bg-yellow-900/30 hover:bg-yellow-200 dark:hover:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300 border border-yellow-300 dark:border-yellow-900/40 transition-colors disabled:opacity-50"
                >
                  <ExternalLink className="h-4 w-4" />
                  {connectMutation.isPending ? 'Reconnecting...' : 'Reconnect'}
                </motion.button>
              )}
              {integration.isConnected && !isStale && (
                <button
                  onClick={handleReconnect}
                  disabled={connectMutation.isPending}
                  className="flex items-center gap-1 text-meta text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors disabled:opacity-50"
                >
                  <RefreshCw className="h-4 w-4" />
                  {connectMutation.isPending ? 'Reauthorizing...' : 'Reauthorize'}
                </button>
              )}
              {integration.isConnected ? (
                <>
                  {isSyncing ? (
                    <button
                      onClick={() => setShowCancelSyncConfirm(true)}
                      className="flex items-center gap-1.5 text-meta px-3 py-2 rounded-lg bg-primary/10 dark:bg-primary/20 text-primary border border-primary/30 hover:bg-primary/20 dark:hover:bg-primary/30 transition-colors"
                    >
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      Syncing…
                    </button>
                  ) : (
                    <motion.button
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                      onClick={() => syncMutation.mutate()}
                      disabled={syncMutation.isPending}
                      className="flex items-center gap-1.5 text-meta px-3 py-2 rounded-lg bg-gray-100 dark:bg-white/10 hover:bg-gray-200 dark:hover:bg-white/15 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-white/20 transition-colors disabled:opacity-50"
                    >
                      <RefreshCw className={`h-4 w-4 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
                      Sync Now
                    </motion.button>
                  )}
                  <motion.button
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => { setDisconnectError(null); setShowDisconnectConfirm(true); }}
                    className="flex items-center gap-1.5 text-meta px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400 transition-colors border border-red-200 dark:border-red-900/30"
                  >
                    <Unlink className="h-4 w-4" />
                    Disconnect
                  </motion.button>
                </>
              ) : (
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={handleConnect}
                  disabled={connectMutation.isPending}
                  className="flex items-center gap-1.5 text-meta px-3 py-2 rounded-lg bg-primary/20 hover:bg-primary/30 text-primary transition-colors border border-primary/30 disabled:opacity-50"
                >
                  <ExternalLink className="h-4 w-4" />
                  {connectMutation.isPending ? 'Connecting...' : 'Connect'}
                </motion.button>
              )}
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isNewlyConnected && !verifyFailed && (
          <SyncPromptModal
            providerName={info.name}
            onSync={() => syncMutation.mutate()}
            onSkip={onSyncPromptDismissed}
            loading={syncMutation.isPending}
          />
        )}
        {showDisconnectConfirm && (
          <ConfirmModal
            title={`Disconnect ${info.name}?`}
            body="This will remove the connection and stop future syncs. Existing synced tickets will not be deleted."
            confirmLabel="Disconnect"
            confirmClassName="bg-red-600 hover:bg-red-700 text-white"
            onConfirm={() => disconnectMutation.mutate()}
            onCancel={() => setShowDisconnectConfirm(false)}
            loading={disconnectMutation.isPending}
          />
        )}
        {showCancelSyncConfirm && (
          <ConfirmModal
            title="Cancel sync?"
            body="The current sync will be stopped. You can restart it manually at any time."
            confirmLabel="Cancel Sync"
            confirmClassName="bg-gray-800 dark:bg-white/10 hover:bg-gray-900 dark:hover:bg-white/20 text-white"
            onConfirm={() => cancelSyncMutation.mutate()}
            onCancel={() => setShowCancelSyncConfirm(false)}
            loading={cancelSyncMutation.isPending}
          />
        )}
        {showDCWizard && (
          <JiraDCWizard
            onConnected={handleDCWizardConnected}
            onCloudInstead={handleCloudInstead}
            onClose={() => setShowDCWizard(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
}

// ---------------------------------------------------------------------------
// "What OctoPM read from your account" surface
// ---------------------------------------------------------------------------

function formatWhen(ts: string | null | undefined): string {
  if (!ts) return 'Unknown time';
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? 'Unknown time' : d.toLocaleString();
}

function IngestedContentModal({ onClose }: { onClose: () => void }) {
  const { data: items, isLoading, isError } = useQuery({
    queryKey: queryKeys.personalGoogleIngestedContent,
    queryFn: getPersonalGoogleIngestedContent,
  });

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 10 }}
        transition={{ duration: 0.15 }}
        className="relative bg-white dark:bg-bg-surface border border-gray-200 dark:border-white/10 rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] z-10 overflow-hidden flex flex-col"
      >
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-100 dark:border-white/[0.06]">
          <div>
            <h3 className="text-title text-gray-900 dark:text-white">What OctoPM read from your account</h3>
            <p className="text-meta text-gray-500 dark:text-gray-400 mt-0.5">
              Every item ingested from your connected Google account, and when.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-white transition-colors flex-shrink-0">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="overflow-y-auto px-6 py-4 space-y-3">
          {isLoading && (
            <div className="space-y-2">
              {[...Array(3)].map((_, i) => <div key={i} className="skeleton h-16 rounded-xl" />)}
            </div>
          )}
          {isError && (
            <div className="flex items-start gap-1.5 text-meta text-red-400 bg-red-900/20 border border-red-900/30 rounded-lg p-3">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              Could not load ingested content. Try again in a moment.
            </div>
          )}
          {!isLoading && !isError && (items?.length ?? 0) === 0 && (
            <p className="text-body text-gray-500 dark:text-gray-400 py-6 text-center">
              Nothing has been ingested from your account yet.
            </p>
          )}
          {!isLoading && !isError && items?.map((item: IngestedContentItem, idx: number) => (
            <div key={idx} className="rounded-xl border border-gray-200 dark:border-white/10 p-3">
              <div className="flex items-start justify-between gap-2">
                <p className="text-body font-medium text-gray-900 dark:text-white">{item.title}</p>
                {item.hasTranscript && (
                  <span className="flex items-center gap-1 text-meta px-1.5 py-0.5 rounded-md bg-gray-100 dark:bg-white/10 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-white/10 flex-shrink-0">
                    Transcript captured
                  </span>
                )}
              </div>
              <p className="text-meta text-gray-500 dark:text-gray-400 mt-1">
                From Calendar · Project: {item.projectName}
              </p>
              <p className="text-meta text-gray-400 dark:text-gray-500 mt-0.5">
                Ingested {formatWhen(item.ingestedAt)}
              </p>
            </div>
          ))}
        </div>
      </motion.div>
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Personal Google Workspace card
// ---------------------------------------------------------------------------

const VISIBILITY_LEVEL_INFO: Record<GoogleVisibilityLevel, { label: string; description: string }> = {
  personal: {
    label: 'Personal (writes only)',
    description: 'OctoPM can create docs, slides, and events on your behalf, but never reads your Calendar or Drive.',
  },
  project_signals: {
    label: 'Project signals (default)',
    description: 'Ingested signals feed project risk scoring. Insights are attributed to the project, not to you by name.',
  },
  full: {
    label: 'Full',
    description: 'Derived insights may name the specific meeting or document they came from.',
  },
};

function PersonalGoogleCard({ integration }: { integration: PersonalIntegration | undefined }) {
  const qc = useQueryClient();
  const [connectError, setConnectError] = useState<string | null>(null);
  const [showDisconnect, setShowDisconnect] = useState(false);
  const [showIngestedContent, setShowIngestedContent] = useState(false);

  const isConnected = integration?.isConnected === true;
  const needsReconsent = isConnected && integration?.needsReconsent === true;
  const visibilityLevel: GoogleVisibilityLevel = integration?.visibilityLevel ?? 'project_signals';

  const visibilityMutation = useMutation({
    mutationFn: (level: GoogleVisibilityLevel) => setPersonalGoogleVisibility(level),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.personalIntegrations }),
  });

  const connectMutation = useMutation({
    mutationFn: getPersonalGoogleConnectUrl,
    onSuccess: ({ url }) => { window.location.href = url; },
    onError: (err: Error) => { setConnectError(err.message || 'Could not start Google authorization.'); },
  });

  const disconnectMutation = useMutation({
    mutationFn: disconnectPersonalGoogle,
    onSuccess: () => {
      setShowDisconnect(false);
      void qc.invalidateQueries({ queryKey: queryKeys.personalIntegrations });
    },
  });

  return (
    <>
      <div className="glass-card rounded-xl p-5">
        <div className="flex items-start gap-4">
          <div className="h-10 w-10 rounded-xl bg-white dark:bg-white/10 flex items-center justify-center flex-shrink-0 overflow-hidden border border-gray-100 dark:border-transparent">
            <ProviderMark provider="google" alt="Google Workspace" className="h-6 w-6" />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div>
                <h4 className="text-title text-gray-900 dark:text-white">Google Workspace</h4>
                <p className="text-meta text-gray-500 dark:text-gray-400 mt-0.5">
                  Connect your Drive, Docs, Calendar, and Meet for personal signal ingestion
                </p>
              </div>
              <span className={`text-meta px-2 py-0.5 rounded-full border flex-shrink-0 flex items-center gap-1 ${
                isConnected
                  ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 border-green-200 dark:border-green-900/30'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-500 border-gray-200 dark:border-gray-700'
              }`}>
                {isConnected ? <><CheckCircle2 className="h-4 w-4" />Connected</> : 'Not Connected'}
              </span>
            </div>

            {isConnected && integration?.metadata.accountEmail && (
              <p className="mt-2 text-meta text-gray-500 dark:text-gray-400">
                Account: {integration.metadata.accountEmail}
              </p>
            )}

            {needsReconsent && (
              <div className="mt-2 flex items-start gap-1.5 text-meta text-yellow-700 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-900/30 rounded-lg p-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>
                  You previously granted broader access than OctoPM now requests (e.g. full Drive
                  or Gmail read access). Reconnect to update your permissions to only what's
                  actually used — your existing connection keeps working until you do.
                  <button
                    onClick={() => { setConnectError(null); connectMutation.mutate(); }}
                    disabled={connectMutation.isPending}
                    className="ml-1 underline hover:no-underline disabled:opacity-50"
                  >
                    {connectMutation.isPending ? 'Reconnecting…' : 'Reconnect now'}
                  </button>
                </span>
              </div>
            )}

            {connectError && (
              <div className="mt-2 flex items-start gap-1.5 text-meta text-red-400 bg-red-900/20 border border-red-900/30 rounded-lg p-2">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                {connectError}
              </div>
            )}

            {isConnected && (
              <div className="mt-3">
                <label htmlFor="google-visibility-level" className="block text-meta font-medium text-gray-600 dark:text-gray-400 mb-1">
                  What OctoPM can see
                </label>
                <select
                  id="google-visibility-level"
                  value={visibilityLevel}
                  onChange={(e) => visibilityMutation.mutate(e.target.value as GoogleVisibilityLevel)}
                  disabled={visibilityMutation.isPending}
                  className="w-full text-meta px-2 py-1.5 rounded-lg bg-gray-100 dark:bg-white/10 border border-gray-200 dark:border-white/10 text-gray-700 dark:text-gray-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 disabled:opacity-50"
                >
                  {(Object.keys(VISIBILITY_LEVEL_INFO) as GoogleVisibilityLevel[]).map((level) => (
                    <option key={level} value={level}>{VISIBILITY_LEVEL_INFO[level].label}</option>
                  ))}
                </select>
                <p className="text-micro text-gray-400 dark:text-gray-500 mt-1">
                  {VISIBILITY_LEVEL_INFO[visibilityLevel].description}
                </p>
              </div>
            )}

            <div className="flex items-center gap-2 mt-3 flex-wrap">
              {isConnected ? (
                <>
                  <button
                    onClick={() => setShowIngestedContent(true)}
                    className="flex items-center gap-1.5 text-meta px-3 py-2 rounded-lg bg-gray-100 dark:bg-white/10 hover:bg-gray-200 dark:hover:bg-white/15 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-white/20 transition-colors"
                  >
                    <FileClock className="h-4 w-4" />
                    View what was read
                  </button>
                  <motion.button
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => setShowDisconnect(true)}
                    className="flex items-center gap-1.5 text-meta px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400 transition-colors border border-red-200 dark:border-red-900/30"
                  >
                    <Unlink className="h-4 w-4" />
                    Disconnect
                  </motion.button>
                </>
              ) : (
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => { setConnectError(null); connectMutation.mutate(); }}
                  disabled={connectMutation.isPending}
                  className="flex items-center gap-1.5 text-meta px-3 py-2 rounded-lg bg-primary/20 hover:bg-primary/30 text-primary transition-colors border border-primary/30 disabled:opacity-50"
                >
                  <ExternalLink className="h-4 w-4" />
                  {connectMutation.isPending ? 'Connecting...' : 'Connect'}
                </motion.button>
              )}
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showDisconnect && (
          <ConfirmModal
            title="Disconnect Google Workspace?"
            body="Your personal Google Workspace connection will be removed and revoked at Google. Signals already ingested from your account will be deleted."
            confirmLabel="Disconnect"
            confirmClassName="bg-red-600 text-white hover:bg-red-700"
            onConfirm={() => disconnectMutation.mutate()}
            onCancel={() => setShowDisconnect(false)}
            loading={disconnectMutation.isPending}
          />
        )}
        {showIngestedContent && (
          <IngestedContentModal onClose={() => setShowIngestedContent(false)} />
        )}
      </AnimatePresence>
    </>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface IntegrationsProps {
  newlyConnected?: string | null;
  // True when the reconnect completed the OAuth handshake but the backend's
  // eager verification (see backend/app/routers/integrations.py) could not
  // confirm the new credential actually works.
  newlyConnectedVerifyFailed?: boolean;
  onSyncPromptDismissed?: () => void;
}

export function Integrations({ newlyConnected, newlyConnectedVerifyFailed, onSyncPromptDismissed }: IntegrationsProps) {
  const { data: integrations, isLoading: workspaceLoading } = useQuery({
    queryKey: queryKeys.integrations,
    queryFn: listIntegrations,
    refetchInterval: (query) => {
      const data = query.state.data;
      const anySyncing = Array.isArray(data) && data.some((i) => i.syncStatus === 'syncing');
      return anySyncing ? 3000 : false;
    },
  });

  const { data: personalIntegrations, isLoading: personalLoading } = useQuery({
    queryKey: queryKeys.personalIntegrations,
    queryFn: listPersonalIntegrations,
  });

  // Health map is best-effort — failures should never block the settings page.
  const { data: healthMap } = useQuery({
    queryKey: queryKeys.integrationHealth,
    queryFn: getIntegrationHealth,
    // Poll occasionally so a successful reconnect flips the badge without a refresh.
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const isLoading = workspaceLoading || personalLoading;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => <div key={i} className="skeleton h-28 rounded-xl" />)}
        </div>
        <div className="space-y-4">
          <div className="skeleton h-28 rounded-xl" />
        </div>
      </div>
    );
  }

  // Workspace integrations: Slack, Jira, Linear (entity-level, admin/PM managed)
  const workspaceProviders: Integration['provider'][] = ['slack', 'jira', 'linear'];
  const displayWorkspaceIntegrations = workspaceProviders.map((provider) => {
    const existing = integrations?.find((i) => i.provider === provider);
    if (existing) return existing;
    return {
      provider,
      isConnected: false,
      connectedByUserId: null,
      connectedAt: null,
      syncSchedule: '1h' as const,
      metadata: {},
      syncStatus: 'idle' as const,
      lastSyncedAt: null,
      lastSyncError: null,
    };
  });

  const personalGoogle = personalIntegrations?.find((i) => i.provider === 'google');

  return (
    <div className="space-y-8">
      {/* Workspace integrations */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <h3 className="text-title text-gray-900 dark:text-white">Workspace Integrations</h3>
        </div>
        <p className="text-body text-gray-500 dark:text-gray-400 mb-4 max-w-2xl">
          Connect your team's shared tools. These are managed by admins and apply across your entire workspace.
        </p>
        <div className="space-y-4">
          {displayWorkspaceIntegrations.map((integration) => (
            <ProviderCard
              key={integration.provider}
              integration={integration}
              isNewlyConnected={newlyConnected === integration.provider}
              verifyFailed={newlyConnected === integration.provider && !!newlyConnectedVerifyFailed}
              onSyncPromptDismissed={onSyncPromptDismissed ?? (() => {})}
              healthStatus={healthMap?.[integration.provider]?.status}
            />
          ))}
        </div>
      </div>

      {/* Personal integrations */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <User className="h-4 w-4 text-gray-400" />
          <h3 className="text-title text-gray-900 dark:text-white">Personal Integrations</h3>
        </div>
        <p className="text-body text-gray-500 dark:text-gray-400 mb-4 max-w-2xl">
          Connect your own accounts so OctoPM can read signals from your personal Drive, Calendar, and Meet activity.
        </p>
        <div className="space-y-4">
          <PersonalGoogleCard integration={personalGoogle} />
        </div>
      </div>
    </div>
  );
}
